import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { formatUserLevel } from '@/utils/formatters';
import { 
  User, 
  Mail, 
  Hospital,
  Calendar,
  Shield,
  Key,
  Save,
  Edit2,
  AlertTriangle,
  Check,
  X,
  Clock,
  MapPin,
  Briefcase
} from 'lucide-react';

interface ProfileData {
  id: number;
  username: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  specialty?: string | null;
  user_level: {
    id: number;
    level_code: string;
    level_name: string;
    level_order: number;
  };
  hospital?: {
    id: number;
    name: string;
    code: string;
    city: string;
  } | null;
  created_at: string;
  last_login?: string | null;
}

export const UserProfile: React.FC = () => {
  const { utilizator } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  
  // Form data for editing
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    specialty: ''
  });
  
  // Password change form
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    try {
      setError(null);
      const response = await api.get<{ user: ProfileData }>('/api/users/profile');
      
      if (response.success && response.data?.user) {
        const userData = response.data.user;
        setProfileData(userData);
        setFormData({
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          specialty: userData.specialty || ''
        });
      }
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading profile:', err);
      setError(err.message || 'Eroare la încărcarea profilului');
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      setError(null);
      
      // Validate required fields
      if (!formData.first_name || !formData.last_name) {
        setError('Prenumele și numele sunt obligatorii');
        return;
      }
      
      const response = await api.put('/api/users/profile', formData);
      
      if (response.success) {
        setSuccessMessage('Profil actualizat cu succes!');
        setIsEditing(false);
        await loadProfileData();
        
        // Note: To fully update the displayed name in the header, 
        // the user may need to refresh the page or we could implement
        // a context update function in the future
        
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Eroare la actualizarea profilului');
    }
  };

  const handleChangePassword = async () => {
    try {
      setPasswordError(null);

      // Validate passwords
      if (!passwordData.current_password || !passwordData.new_password) {
        setPasswordError('Toate câmpurile sunt obligatorii');
        return;
      }

      if (passwordData.new_password !== passwordData.confirm_password) {
        setPasswordError('Parolele noi nu coincid');
        return;
      }

      if (passwordData.new_password.length < 8) {
        setPasswordError('Parola nouă trebuie să aibă cel puțin 8 caractere');
        return;
      }

      const response = await api.post('/api/users/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });

      if (response.success) {
        setSuccessMessage('Parolă schimbată cu succes!');
        setShowPasswordDialog(false);
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      setPasswordError(err.message || 'Eroare la schimbarea parolei');
    }
  };

  const closePasswordDialog = () => {
    setShowPasswordDialog(false);
    setPasswordError(null);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setError(null);
    // Reset form data to original values
    if (profileData) {
      setFormData({
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        specialty: profileData.specialty || ''
      });
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-6 md:grid-cols-3">
            <div className="md:col-span-2">
              <Skeleton className="h-96" />
            </div>
            <div>
              <Skeleton className="h-64" />
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!profileData) {
    return (
      <MainLayout>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Nu s-au putut încărca datele profilului</AlertDescription>
        </Alert>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Profilul meu</h1>
            <p className="text-muted-foreground">
              Gestionează informațiile personale și setările contului
            </p>
          </div>
          {!isEditing && (
            <Button onClick={() => setIsEditing(true)}>
              <Edit2 className="mr-2 h-4 w-4" />
              Editează profil
            </Button>
          )}
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {successMessage && (
          <Alert className="border-green-500 bg-green-50">
            <Check className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-6 md:grid-cols-3">
          {/* Main Profile Card */}
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Informații personale</CardTitle>
                <CardDescription>
                  Datele tale personale și de contact
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="info" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="info">Informații</TabsTrigger>
                    <TabsTrigger value="security">Securitate</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="info" className="space-y-4">
                    <div className="grid gap-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="first_name">
                            <User className="inline h-4 w-4 mr-1" />
                            Prenume
                          </Label>
                          {isEditing ? (
                            <Input
                              id="first_name"
                              value={formData.first_name}
                              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                            />
                          ) : (
                            <p className="text-sm font-medium">{profileData.first_name || 'Necompletat'}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="last_name">
                            <User className="inline h-4 w-4 mr-1" />
                            Nume
                          </Label>
                          {isEditing ? (
                            <Input
                              id="last_name"
                              value={formData.last_name}
                              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                            />
                          ) : (
                            <p className="text-sm font-medium">{profileData.last_name || 'Necompletat'}</p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>
                          <Mail className="inline h-4 w-4 mr-1" />
                          Email
                        </Label>
                        <p className="text-sm font-medium">{profileData.email}</p>
                        {isEditing && (
                          <p className="text-xs text-muted-foreground">Email-ul nu poate fi modificat</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="specialty">
                          <Briefcase className="inline h-4 w-4 mr-1" />
                          Specialitate
                        </Label>
                        {isEditing ? (
                          <Input
                            id="specialty"
                            value={formData.specialty}
                            onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                            placeholder="ex: Cardiologie Pediatrică"
                          />
                        ) : (
                          <p className="text-sm font-medium">{profileData.specialty || 'Nespecificat'}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>
                          <Hospital className="inline h-4 w-4 mr-1" />
                          Spital
                        </Label>
                        <p className="text-sm font-medium">
                          {profileData.hospital ? `${profileData.hospital.name} (${profileData.hospital.code})` : 'Nu este asociat cu un spital'}
                        </p>
                        {profileData.hospital?.city && (
                          <p className="text-xs text-muted-foreground">
                            <MapPin className="inline h-3 w-3 mr-1" />
                            {profileData.hospital.city}
                          </p>
                        )}
                      </div>

                      {isEditing && (
                        <div className="flex gap-2 pt-4">
                          <Button onClick={handleUpdateProfile}>
                            <Save className="mr-2 h-4 w-4" />
                            Salvează
                          </Button>
                          <Button variant="outline" onClick={cancelEdit}>
                            <X className="mr-2 h-4 w-4" />
                            Anulează
                          </Button>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="security" className="space-y-4">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Nume utilizator</Label>
                        <p className="text-sm font-medium">{profileData.username}</p>
                      </div>

                      <div className="space-y-2">
                        <Label>Parolă</Label>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-muted-foreground">••••••••</p>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => { setPasswordError(null); setShowPasswordDialog(true); }}
                          >
                            <Key className="mr-2 h-4 w-4" />
                            Schimbă parola
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Ultima autentificare</Label>
                        <p className="text-sm font-medium">
                          {profileData.last_login 
                            ? new Date(profileData.last_login).toLocaleString('ro-RO')
                            : 'Nu există înregistrări'}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>Cont creat</Label>
                        <p className="text-sm font-medium">
                          {new Date(profileData.created_at).toLocaleDateString('ro-RO')}
                        </p>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Side Cards */}
          <div className="space-y-6">
            {/* Account Status Card */}
            <Card>
              <CardHeader>
                <CardTitle>Status cont</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Nivel acces</span>
                  <Badge variant="secondary">
                    <Shield className="mr-1 h-3 w-3" />
                    {formatUserLevel(profileData.user_level.id)}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">ID utilizator</span>
                  <span className="text-sm font-medium">#{profileData.id}</span>
                </div>
              </CardContent>
            </Card>

            {/* Activity Summary Card */}
            <Card>
              <CardHeader>
                <CardTitle>Activitate recentă</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Ultima activitate:</span>
                  </div>
                  <p className="text-sm font-medium">
                    {profileData.last_login 
                      ? `${new Date(profileData.last_login).toLocaleDateString('ro-RO')} la ${new Date(profileData.last_login).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}`
                      : 'Nu există înregistrări'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions Card */}
            <Card>
              <CardHeader>
                <CardTitle>Acțiuni rapide</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => { setPasswordError(null); setShowPasswordDialog(true); }}
                >
                  <Key className="mr-2 h-4 w-4" />
                  Schimbă parola
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit2 className="mr-2 h-4 w-4" />
                  Editează profil
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Change Password Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={(open) => { if (!open) closePasswordDialog(); else setShowPasswordDialog(true); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Schimbă parola</DialogTitle>
              <DialogDescription>
                Introdu parola curentă și noua parolă dorită
              </DialogDescription>
            </DialogHeader>
            {passwordError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Parola curentă</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">Parola nouă</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  placeholder="Minim 8 caractere"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirmă parola nouă</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closePasswordDialog}>
                Anulează
              </Button>
              <Button onClick={handleChangePassword}>
                Schimbă parola
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};